import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "telephone-tombe-dans-eau",
  silo: "telephone",
  h1: "Téléphone tombé dans l'eau : les bons gestes dans les premières minutes",
  metaTitle: "Téléphone tombé dans l'eau : que faire en urgence ?",
  metaDescription: "Votre téléphone est tombé dans l'eau ? Découvrez les gestes à faire immédiatement, les erreurs à éviter (riz, sèche-cheveux) et vos chances de réparation.",
  excerpt: "Les premières minutes après une chute dans l'eau sont décisives pour sauver votre téléphone. Voici les bons réflexes et les pièges à éviter absolument.",
  publishedAt: "2026-08-25",
  readingMinutes: 6,
  relatedSlugs: ["telephone-ne-s-allume-plus", "telephone-ne-charge-plus", "batterie-telephone-se-decharge-vite", "ecran-telephone-casse"],
  faq: [
    {
      question: "Le riz permet-il vraiment de sauver un téléphone mouillé ?",
      answer: "Non, c'est un mythe très répandu. Le riz absorbe un peu d'humidité en surface mais n'assèche pas les composants internes en profondeur, et ses grains ou sa poussière peuvent se coincer dans les ports et haut-parleurs. Un déshydratant en gel ou un passage chez un professionnel est bien plus efficace.",
    },
    {
      question: "Peut-on utiliser un sèche-cheveux pour sécher un téléphone mouillé ?",
      answer: "Non, c'est fortement déconseillé. La chaleur peut endommager la batterie et les composants électroniques, et le souffle d'air risque de pousser l'eau plus profondément dans l'appareil au lieu de l'évacuer.",
    },
    {
      question: "Combien de temps faut-il attendre avant de rallumer un téléphone mouillé ?",
      answer: "Il n'y a pas de délai universel garanti sans risque, mais il vaut mieux ne surtout pas rallumer l'appareil avant qu'il soit complètement sec, ce qui peut prendre au minimum 24 à 48 heures. Un diagnostic professionnel rapide reste la meilleure option pour éviter d'aggraver les dégâts.",
    },
    {
      question: "Un téléphone certifié étanche peut-il quand même être endommagé par l'eau ?",
      answer: "Oui. La certification d'étanchéité (indice IP) correspond à des conditions de test précises et se dégrade avec le temps, les chocs ou l'usure des joints. Une immersion prolongée, de l'eau chaude ou salée peuvent dépasser ce que l'étanchéité était censée supporter.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Toilettes, piscine, verre renversé ou pluie soudaine : un téléphone tombé dans l'eau est une situation stressante où chaque minute compte. Contrairement aux idées reçues, la plupart des réflexes populaires ne sont pas efficaces, voire aggravent les dégâts. Voici la marche à suivre.",
    },
    {
      type: "h2",
      text: "Les gestes à faire immédiatement",
    },
    {
      type: "ol",
      items: [
        "Sortez le téléphone de l'eau le plus rapidement possible",
        "Éteignez-le immédiatement s'il est encore allumé, sans appuyer sur d'autres boutons inutilement",
        "Essuyez l'extérieur avec un chiffon doux et absorbant, sans frotter",
        "Retirez la coque, la carte SIM et tout accessoire amovible pour permettre à l'humidité de s'évacuer",
        "Secouez très légèrement l'appareil, écran vers le bas, pour aider l'eau à s'écouler des ouvertures",
        "Ne rebranchez surtout pas le chargeur avant d'être certain que l'appareil est complètement sec",
        "Amenez le téléphone chez un professionnel dès que possible pour un nettoyage interne et un diagnostic",
      ],
    },
    {
      type: "callout",
      variant: "warning",
      title: "N'allumez pas un téléphone éteint qui a pris l'eau",
      text: "Si le téléphone s'est déjà éteint tout seul après la chute, ne tentez pas de le rallumer avant qu'il soit expertisé. Faire passer du courant dans un circuit encore humide est l'une des principales causes de court-circuit irréversible.",
    },
    {
      type: "h2",
      text: "Ce qu'il ne faut surtout pas faire",
    },
    {
      type: "ul",
      items: [
        "Le riz : il absorbe un peu d'humidité en surface mais n'assèche pas les composants internes, et sa poussière peut boucher les ports et haut-parleurs. C'est un mythe qui retarde souvent une vraie prise en charge",
        "Le sèche-cheveux : la chaleur peut endommager la batterie et pousser l'eau plus profondément dans l'appareil au lieu de l'évacuer",
        "Le four ou le radiateur : une chaleur excessive peut détruire définitivement la batterie et les composants électroniques",
        "Rallumer le téléphone \"pour voir\" : c'est le geste le plus risqué, car il peut provoquer un court-circuit sur des circuits encore humides",
        "Recharger l'appareil avant qu'il soit sec : cela multiplie le risque de dommage électrique",
      ],
    },
    {
      type: "callout",
      variant: "info",
      title: "Pourquoi le riz est un mythe tenace",
      text: "Le riz est capable d'absorber de l'humidité ambiante, mais il n'a aucun effet sur l'eau déjà infiltrée entre les composants d'un circuit imprimé. Un déshydratant en gel de silice est plus efficace, mais rien ne remplace un démontage et un nettoyage professionnel pour les cas sérieux.",
    },
    {
      type: "h2",
      text: "Pourquoi le délai d'intervention est critique",
    },
    {
      type: "p",
      text: "L'eau elle-même n'est pas toujours le problème principal : ce sont les résidus qu'elle contient (minéraux, sel, sucre selon le liquide) qui, une fois secs, peuvent provoquer de la corrosion sur les composants. Plus le téléphone reste humide longtemps sans intervention, plus le risque de corrosion et de dommages irréversibles augmente. C'est pourquoi il est important d'agir vite, sans pour autant précipiter une remise sous tension prématurée.",
    },
    {
      type: "h2",
      text: "Vos chances de réparation",
    },
    {
      type: "p",
      text: "Contrairement à une idée reçue, un téléphone tombé dans l'eau n'est pas automatiquement perdu. Beaucoup d'appareils pris en charge rapidement et correctement nettoyés en interne retrouvent un fonctionnement normal. Les facteurs qui influencent le plus les chances de réparation sont la rapidité d'intervention, le type de liquide (l'eau claire est moins agressive que l'eau salée ou sucrée), et la durée d'immersion.",
    },
    {
      type: "p",
      text: "Un technicien commence toujours par un diagnostic complet : état de la batterie, de la carte mère, des connecteurs et de l'[écran](slug:ecran-telephone-casse). Si le téléphone ne redémarre plus après le passage dans l'eau, consultez également notre article sur le [téléphone qui ne s'allume plus](slug:telephone-ne-s-allume-plus) pour comprendre les étapes de diagnostic. Un nettoyage professionnel des circuits, suivi de tests approfondis, permet souvent de sauver l'appareil sans qu'un remplacement complet soit nécessaire.",
    },
  ],
};

export default article;
